import { zodResolver } from '@hookform/resolvers/zod'
import { IconPlus } from '@tabler/icons-react'
import { useCallback, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton'
import { SecondaryButton } from '../../atoms/buttons/SecondaryButton'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../atoms/dialog'
import { Input } from '../../atoms/inputs/Input'
import { api } from 'src/api/root'
import Typography from '../../atoms/Typography'
import { Checkbox } from 'src/components/atoms/buttons/CheckBoxButton'
import TagSelector from '../TypeTagSelector'
import StageSelector from '../StageSelector'
import { nameRegex, projectName } from 'src/enviroment/validation/fields'

const defaultValues: AddProjectFormData = {
  name: '',
  location: '',
  mapUrl: '',
  totalArea: 0,
  typeIds: [],
  stageId: null,
  isPublic: false,
}

const AddProjectSchema = z.object({
  name: projectName,
  location: z
    .string()
    .trim()
    .min(3, { message: 'Country must be at least 3 characters' })
    .max(35, { message: 'Country cannot exceed 35 characters' })
    .regex(nameRegex, { message: 'Country must contain only letters and spaces' }),
  mapUrl: z.string().url({ message: 'Invalid URL' }),
  totalArea: z.number({ coerce: true }).positive({ message: 'Area size must be a positive number' }),
  // .max(1000000, { message: 'Area size cannot exceed 1,000,000' }),
  typeIds: z.array(z.string()).default([]),
  stageId: z.string().nullable(),
  isPublic: z.boolean().default(false),
})

export type AddProjectFormData = z.infer<typeof AddProjectSchema>

const AddProjectDialog = () => {
  const [opened, setOpened] = useState(false)
  const handleClose = useCallback(() => setOpened(false), [])

  const form = useForm<AddProjectFormData>({
    mode: 'all',
    defaultValues,
    reValidateMode: 'onChange',
    resolver: zodResolver(AddProjectSchema),
  })

  const { mutateAsync: createNewProject } = api.superAdmin.addProject.useMutation()

  const handleSubmit: SubmitHandler<AddProjectFormData> = useCallback(async (data) => {
    await createNewProject(data)
    setOpened(false)
    form.reset()
  }, [])

  return (
    <Dialog open={opened} onOpenChange={setOpened}>
      <DialogTrigger asChild>
        <SecondaryButton className="flex-shrink-0" leftIcon={<IconPlus />}>
          Add Project
        </SecondaryButton>
      </DialogTrigger>
      <DialogContent aria-description="add-project-dialog">
        <DialogHeader>
          <DialogTitle>
            <Typography variant="2xl-bold">Add Project</Typography>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <Input
              type="text"
              disabled={form.formState.isSubmitting}
              autoComplete="off"
              errorMessage={form.formState.errors.name?.message}
              label="Name of Project"
              placeholder="Name of Project"
              {...form.register('name')}
            />
          </div>
          <div>
            <Input
              type="text"
              disabled={form.formState.isSubmitting}
              autoComplete="off"
              errorMessage={form.formState.errors.location?.message}
              label="Location"
              placeholder="Angola"
              {...form.register('location')}
            />
          </div>
          <div>
            <Input
              type="text"
              disabled={form.formState.isSubmitting}
              autoComplete="off"
              errorMessage={form.formState.errors.mapUrl?.message}
              label="Map Link"
              placeholder="https://felt.com/map/Deforestation-Risk-G2K46e8kT9CWoinzt7rxWfB?loc=-11.262,17.919,5.5z"
              {...form.register('mapUrl')}
            />
          </div>
          <div>
            <Input
              type="number"
              disabled={form.formState.isSubmitting}
              autoComplete="off"
              label="Area Size"
              errorMessage={form.formState.errors.totalArea?.message}
              placeholder="9339.339"
              {...form.register('totalArea')}
            />
          </div>
          <div className="col-span-2">
            <TagSelector
              onTypesChange={(typeIds) => form.setValue('typeIds', typeIds)}
              label="Types"
              placeholder="Search types"
              isAdmin
              errorMessage={form.formState.errors.typeIds?.message}
            />
          </div>
          <div className="col-span-2">
            <StageSelector
              onStageChange={(stageId) => form.setValue('stageId', stageId)}
              label="Stage"
              placeholder="Search stages"
              errorMessage={form.formState.errors.stageId?.message}
            />
          </div>
          <div className="col-span-2 flex items-center space-x-2">
            <Controller
              name="isPublic"
              control={form.control}
              render={({ field }) => (
                <Checkbox
                  id="isPublic"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={form.formState.isSubmitting}
                  errorMessage={form.formState.errors.isPublic?.message}
                  label="Make project as public"
                />
              )}
            />
          </div>
        </div>
        <DialogFooter className="sm:justify-center sm:space-x-6">
          <SecondaryButton isLoading={form.formState.isSubmitting} onClick={handleClose} className="w-40">
            Back
          </SecondaryButton>
          <PrimaryButton
            onClick={form.handleSubmit(handleSubmit)}
            isLoading={form.formState.isSubmitting}
            className="w-40"
          >
            Create
          </PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AddProjectDialog
